import { dtTokenScope, dtFocusRing } from '../dev-tools/css-recipes';

/**
 * FeatureFlagsSection CSS — shipped as an injected style string (the platform build has
 * no css-loader, so an SFC `<style>` block cannot be parsed).
 *
 * Design contract lives in `../dev-tools/css-recipes.ts`: this file consumes
 * `var(--dt-*)` exclusively. The section declares its own token scope because it can
 * mount outside the panel (dev-shell embeds it directly in its Inspector tab).
 *
 * Every selector is namespaced under `.abff` (the section root) to prevent leakage.
 */
export const FEATURE_FLAGS_SECTION_CSS = /* css */ `
${dtTokenScope('.abff')}

.abff {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: var(--dt-text-sm);
  color: var(--dt-fg);
}

/* ── Toolbar ─────────────────────────────────────────────── */
.abff__bar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.abff__search {
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
}

.abff__search-icon {
  position: absolute;
  left: 8px;
  width: 13px;
  height: 13px;
  fill: none;
  stroke: var(--dt-muted-fg);
  stroke-width: 2;
  stroke-linecap: round;
  pointer-events: none;
}

.abff__search-input {
  flex: 1;
  min-width: 0;
  height: 28px;
  padding: 0 26px;
  background: var(--dt-surface);
  color: var(--dt-fg);
  border: 1px solid var(--dt-border);
  border-radius: var(--dt-radius-sm);
  font-family: inherit;
  font-size: var(--dt-text-sm);
  transition: border-color var(--dt-dur-fast) var(--dt-ease),
    box-shadow var(--dt-dur-fast) var(--dt-ease);
}

.abff__search-input::placeholder {
  color: var(--dt-muted-fg);
}

.abff__search-input:focus-visible {
  outline: none;
  border-color: var(--dt-ring);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--dt-ring) 40%, transparent);
}

.abff__search-input::-webkit-search-cancel-button {
  appearance: none;
}

.abff__clear {
  position: absolute;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: var(--dt-radius-sm);
  color: var(--dt-muted-fg);
  cursor: pointer;
  transition: color var(--dt-dur-fast) var(--dt-ease), background var(--dt-dur-fast) var(--dt-ease);
}

.abff__clear:hover {
  color: var(--dt-fg);
  background: color-mix(in srgb, var(--dt-fg) 8%, transparent);
}

${dtFocusRing('.abff__clear', '1px')}

.abff__clear-icon {
  width: 11px;
  height: 11px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
}

/* Provider name: quiet metadata, not a badge competing with the rows. */
.abff__brand {
  flex-shrink: 0;
  color: var(--dt-muted-fg);
  font-size: var(--dt-text-xs);
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
}

/* ── Override command bar ───────────────────────────────── */
.abff__cmdbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  background: color-mix(in srgb, var(--dt-accent) 8%, transparent);
  border: 1px solid color-mix(in srgb, var(--dt-accent) 25%, transparent);
  border-radius: var(--dt-radius);
}

.abff__cmdbar-count {
  color: var(--dt-fg);
  font-size: var(--dt-text-sm);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.abff__cmdbar-note {
  color: var(--dt-muted-fg);
  font-size: var(--dt-text-xs);
}

.abff__cmdbar-spacer {
  flex: 1;
}

.abff__cmdbar-btn {
  padding: 2px 8px;
  background: transparent;
  color: var(--dt-fg);
  border: 1px solid var(--dt-border);
  border-radius: var(--dt-radius-sm);
  font-family: inherit;
  font-size: var(--dt-text-xs);
  font-weight: 500;
  cursor: pointer;
  transition: background var(--dt-dur-fast) var(--dt-ease),
    border-color var(--dt-dur-fast) var(--dt-ease), color var(--dt-dur-fast) var(--dt-ease);
}

.abff__cmdbar-btn:hover {
  background: color-mix(in srgb, var(--dt-fg) 6%, transparent);
  border-color: var(--dt-muted-fg);
}

.abff__cmdbar-btn--danger:hover {
  color: var(--dt-destructive);
  border-color: color-mix(in srgb, var(--dt-destructive) 50%, transparent);
}

${dtFocusRing('.abff__cmdbar-btn', '1px')}

.abff__add {
  align-self: flex-start;
  padding: 4px 10px;
  background: transparent;
  color: var(--dt-muted-fg);
  border: 1px dashed var(--dt-border);
  border-radius: var(--dt-radius-sm);
  font-family: inherit;
  font-size: var(--dt-text-xs);
  cursor: pointer;
  transition: color var(--dt-dur-fast) var(--dt-ease),
    border-color var(--dt-dur-fast) var(--dt-ease);
}

.abff__add:hover {
  color: var(--dt-fg);
  border-color: var(--dt-ring);
}

${dtFocusRing('.abff__add', '1px')}

.abff__add-name {
  color: var(--dt-fg);
  font-family: var(--dt-font-mono);
  font-weight: 600;
}

.abff__hint {
  margin: 2px 0;
  color: var(--dt-muted-fg);
}

/* ── Groups ──────────────────────────────────────────────── */
.abff__group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.abff__group-head {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 4px 2px;
  background: transparent;
  border: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  color: var(--dt-muted-fg);
}

.abff__group-head--static {
  cursor: default;
}

.abff__group-head:not(.abff__group-head--static):hover {
  color: var(--dt-fg);
}

${dtFocusRing('.abff__group-head', '1px')}
.abff__group-head:focus-visible {
  border-radius: var(--dt-radius-sm);
}

.abff__chev {
  display: inline-block;
  font-size: var(--dt-text-xs);
  transition: transform var(--dt-dur-fast) var(--dt-ease);
}

.abff__chev--open {
  transform: rotate(90deg);
}

.abff__group-label {
  font-size: var(--dt-text-xs);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.abff__group-count {
  padding: 0 6px;
  background: var(--dt-muted);
  color: var(--dt-muted-fg);
  border-radius: 999px;
  font-size: var(--dt-text-xs);
  font-variant-numeric: tabular-nums;
  line-height: 1.5;
}

/* ── Rows ────────────────────────────────────────────────── */
.abff__list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.abff__row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 8px;
  border-radius: var(--dt-radius-sm);
  transition: background var(--dt-dur-fast) var(--dt-ease);
}

.abff__row:hover {
  background: color-mix(in srgb, var(--dt-fg) 5%, transparent);
}

.abff__row--ovr {
  background: color-mix(in srgb, var(--dt-accent) 6%, transparent);
  box-shadow: inset 2px 0 0 var(--dt-accent);
}

.abff__row--ovr:hover {
  background: color-mix(in srgb, var(--dt-accent) 10%, transparent);
}

/* Shared value-dot encoding (effective dot + provider-base dot):
   filled accent = on, hollow muted ring = off, dashed = unknown. */
.abff__eff,
.abff__base {
  flex-shrink: 0;
  border-radius: 999px;
  border: 1.5px solid var(--dt-muted-fg);
  background: transparent;
}

.abff__eff {
  width: 8px;
  height: 8px;
}

.abff__base {
  width: 6px;
  height: 6px;
  border-width: 1px;
}

.abff__eff--on,
.abff__base--on {
  border-color: var(--dt-accent);
  background: var(--dt-accent);
}

.abff__base--unknown {
  border-style: dashed;
  opacity: 0.6;
}

.abff__meta {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  gap: 1px;
}

.abff__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--dt-fg);
  font-family: var(--dt-font-mono);
  font-size: var(--dt-text-sm);
}

.abff__desc {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--dt-muted-fg);
  font-size: var(--dt-text-xs);
}

/* Segmented on / auto / off — shadcn toggle-group look. */
.abff__seg {
  display: inline-flex;
  flex-shrink: 0;
  gap: 2px;
  padding: 2px;
  background: var(--dt-muted);
  border-radius: var(--dt-radius);
}

.abff__seg-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: transparent;
  color: var(--dt-muted-fg);
  border: none;
  border-radius: var(--dt-radius-sm);
  font-family: inherit;
  font-size: var(--dt-text-xs);
  font-weight: 600;
  cursor: pointer;
  transition: color var(--dt-dur-fast) var(--dt-ease), background var(--dt-dur-fast) var(--dt-ease),
    box-shadow var(--dt-dur-fast) var(--dt-ease);
}

.abff__seg-btn:hover {
  color: var(--dt-fg);
}

${dtFocusRing('.abff__seg-btn', '1px')}

.abff__seg-btn--active {
  background: var(--dt-bg);
  color: var(--dt-fg);
  box-shadow: 0 1px 2px color-mix(in srgb, #000 18%, transparent);
}
`;
